/**
 * Phase 3 — Local Archive Settings Panel
 *
 * Shows:
 *  - IndexedDB storage usage and count of locally stored messages
 *  - List of locally stored messages (with delete option)
 *  - Button to open the batch offload candidates view
 *  - Note about multi-device limitations
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Divider, CircularProgress,
  List, ListItem, ListItemText, ListItemSecondaryAction, IconButton,
  Alert, Chip, Tooltip, LinearProgress, Stack,
} from '@mui/material';
import DeleteIcon          from '@mui/icons-material/Delete';
import DevicesIcon         from '@mui/icons-material/Devices';
import CloudDownloadIcon   from '@mui/icons-material/CloudDownload';
import FolderZipIcon       from '@mui/icons-material/FolderZip';
import InfoOutlinedIcon    from '@mui/icons-material/InfoOutlined';
import {
  getAllMetadata,
  deleteMessage,
  getTotalUsageBytes,
  OffloadedMeta,
} from '../../services/offloadedMessageStore';
import api from '../../services/api';
import BatchOffloadDialog from './BatchOffloadDialog';

function fmtStorage(b: number) {
  if (b < 1024)           return `${b} B`;
  if (b < 1_048_576)      return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1_073_741_824)  return `${(b / 1_048_576).toFixed(1)} MB`;
  return `${(b / 1_073_741_824).toFixed(2)} GB`;
}

function fmtDate(d: string) {
  if (!d || d.length < 8) return d;
  return `${d.substring(0, 4)}-${d.substring(4, 6)}-${d.substring(6, 8)}`;
}

const BROWSER_LIMIT_ESTIMATE = 500 * 1024 * 1024; // 500 MB rough estimate

const LocalArchivePanel: React.FC = () => {
  const [records, setRecords]           = useState<OffloadedMeta[]>([]);
  const [totalBytes, setTotalBytes]     = useState(0);
  const [loading, setLoading]           = useState(true);
  const [batchOpen, setBatchOpen]       = useState(false);
  const [exporting, setExporting]       = useState(false);
  const [exportError, setExportError]   = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [meta, bytes] = await Promise.all([getAllMetadata(), getTotalUsageBytes()]);
      setRecords(meta.sort((a, b) => b.storedAt.localeCompare(a.storedAt)));
      setTotalBytes(bytes);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = async (mailId: string) => {
    await deleteMessage(mailId);
    await refresh();
  };

  const handleExportZip = async () => {
    setExporting(true);
    setExportError('');
    try {
      const blob = await api.get('/api/offload/export-zip', { responseType: 'blob' }) as unknown as Blob;
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `summa-messages-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Export failed. Make sure your messages are in blob storage.');
    } finally {
      setExporting(false);
    }
  };

  const usagePct = Math.min(100, (totalBytes / BROWSER_LIMIT_ESTIMATE) * 100);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DevicesIcon color="primary" />
        <Typography variant="h6">Local Message Archive</Typography>
      </Box>

      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
        <strong>Browser cache only</strong> — messages stored here live inside your browser's
        built-in database (IndexedDB) on <em>this computer in this browser</em>. They are
        not saved as files on your hard drive and are not accessible from other devices or
        browsers. Clearing your browser data will delete them.
        <br />
        To keep a permanent, portable backup use <strong>Export to ZIP (.eml)</strong> below.
      </Alert>

      {/* Storage Usage */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Storage Usage</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {fmtStorage(totalBytes)} used · {records.length} message{records.length !== 1 ? 's' : ''}
          </Typography>
          {usagePct > 70 && (
            <Chip label={`${usagePct.toFixed(0)}% full`} color="warning" size="small" />
          )}
        </Box>
        <Tooltip title={`${fmtStorage(totalBytes)} of ~${fmtStorage(BROWSER_LIMIT_ESTIMATE)} estimated limit`}>
          <LinearProgress
            variant="determinate"
            value={usagePct}
            color={usagePct > 80 ? 'error' : usagePct > 60 ? 'warning' : 'primary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Tooltip>
      </Paper>

      {/* Actions */}
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
        <Button
          variant="outlined"
          startIcon={<CloudDownloadIcon />}
          onClick={() => setBatchOpen(true)}
        >
          Save to Browser Cache
        </Button>
        <Button
          variant="outlined"
          color="success"
          startIcon={<FolderZipIcon />}
          onClick={handleExportZip}
          disabled={exporting}
        >
          {exporting ? 'Exporting…' : 'Export to ZIP (.eml)'}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        <strong>Save to Browser Cache</strong> — stores messages locally so you can read them
        without retrieval fees. Only on this browser.
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        <strong>Export to ZIP</strong> — downloads your messages as standard .eml files you
        can open in any email client and keep as a permanent backup on your computer.
      </Typography>
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* List */}
      <Typography variant="subtitle2" gutterBottom>
        Locally Stored Messages
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : records.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No messages saved in this browser's cache yet. Use <strong>"Save to Browser Cache"</strong> to
          pull messages from the server into this browser, or use <strong>"Export to ZIP"</strong> to
          download them directly to your computer as .eml files.
        </Typography>
      ) : (
        <List dense disablePadding>
          {records.map(r => (
            <ListItem key={r.mailId} divider sx={{ py: 0.5 }}>
              <ListItemText
                primary={
                  <Typography variant="body2" noWrap sx={{ maxWidth: 340 }}>
                    {r.subject}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    From {r.fromName} · {fmtDate(r.sentDate)} · {fmtStorage(r.sizeBytes)}
                    · Saved {new Date(r.storedAt).toLocaleDateString()}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="Remove from local archive (does not restore server copy)">
                  <IconButton size="small" edge="end" onClick={() => handleDelete(r.mailId)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <BatchOffloadDialog
        open={batchOpen}
        onClose={() => { setBatchOpen(false); refresh(); }}
      />
    </Box>
  );
};

export default LocalArchivePanel;
